/*
 * WHAT IS ALSO RELATED ABOUT THIS PERSON.
 *
 * Hadith on a figure's panel, and deliberately not a fifth scripture. `ROADMAP.md` settled
 * that: putting them beside the Quran in the same vocabulary at the same weight would assert
 * an equivalence Muslim readers do not hold, and no prose on the page would be doing it —
 * the layout would. So these live under their own heading, in the panel, reached from a
 * figure rather than from the map.
 *
 * ARABIC WITH NO ENGLISH, and the reason is stated where a reader meets it. Every other
 * passage in this project shows an original and a translation together. These cannot: no
 * openly-licensed complete English translation of Bukhari or Muslim exists — see SOURCES.md
 * for the editions and their terms. A reader who is shown Arabic alone is owed that on the
 * spot, not in a file nobody opens.
 *
 * THE CHAIN IS SHOWN, because it is what makes a hadith a hadith. A report's weight comes
 * from its isnad, and dropping it would leave the text looking like an anonymous saying.
 * The names are NOT links: a transmitter who shares a prophet's name is not that prophet —
 * إبراهيم in a chain is Ibrahim al-Nakha'i — and linking them was a real defect caught in
 * the build.
 */
import { REPORTS_PER_STEP } from '@versesverse/schema/artifacts';
import { useEffect } from 'react';
import { useLang } from '../i18n/lang';
import { useGrowOnScroll } from './useGrowOnScroll';
import { needsNumberingNote } from './numbering';
import { VerseTranslation } from '../verses/VerseTranslation';
import type { TextTranslator } from '../i18n/translate';
import type { Grade } from '@versesverse/schema/reports';
import { reachesTheProphet, layerTerm, type HadithLayer } from '@versesverse/schema/hadith-layer';

export interface PanelReport {
  /** "Bukhari 152" — the citation the tradition uses. */
  ref: string;
  /** Arabic. Always present. */
  ar: string;
  /** The chain, collector first. Names as the collection gives them. */
  isnad: string[];
  /**
   * WHO EACH LINK IS, positionally — added 2026-08-19.
   *
   * A chain gives the shortest name that identified a man to the people who heard it. `CLAUDE.md`
   * opens on what that costs a reader now: *"إبراهيم appears in 1,955 Bukhārī reports and 1,128 of
   * those are Ibrahim al-Nakhaʿi in the chain, not the prophet."*
   *
   * So where the corpus states the identity, it is shown BESIDE the name and never instead of it —
   * `ابن عجلان` stays, and *Muḥammad b. ʿAjlān al-Qurashī · 148* sits under it. The book says what
   * the book says.
   *
   * Absent on every shelf whose supplier states nothing, and an empty string at any position the
   * corpus did not identify.
   */
  who?: string[];
  /**
   * The same report in the other collection — muttafaq alayh, "agreed upon".
   *
   * The highest standing the tradition gives a report, and the only statement about a
   * hadith's weight this library can make from the text it holds. It replaces a `grade` that
   * was never once populated: both collections are ṣaḥīḥ by definition, so the collection is
   * the grade and the field had nothing to carry.
   *
   * TRUE OF THOSE TWO COLLECTIONS AND APPLIED TO A SHELF OF NINE - corrected 2026-08-18.
   *
   * Bukhari and Muslim are sahih by definition. al-Tirmidhi is the collection famous for grading
   * itself hadith by hadith, and he does: 2,882 of his 3,891 reports carry his verdict IN THE
   * ARABIC THIS LIBRARY ALREADY SHIPS - 1,958 hasan sahih, 694 hasan gharib, 250 sahih, 50 daif.
   * No other collection carries more than fifteen, which is the control: the pattern finds
   * Tirmidhi rather than a common phrase.
   *
   * So the field had nothing to carry because nothing had looked, not because there was nothing.
   * He died in 892, so there is no licence question at all. See SOURCES.md.
   *
   * AND FOR THAT COLLECTION `matn` IS NOT A MATN. The verdict begins 451 characters into a 1,666
   * character field and runs on into who Abu Salih was, so a reader shown the matn is shown
   * Tirmidhi’s apparatus too, with nothing saying where the report stops.
   */
  alsoIn?: string[];
  /** Ayahs this report quotes, resolved from its own text — "Quran 2:143". */
  cites?: string[];
  /**
   * THE EARLIER WORK THIS REPORT WAS QUOTED FROM — added 2026-08-19.
   *
   * Mustadrak al-Wasāʾil is a SUPPLEMENT. Al-Nūrī, who died in 1902, collected what the Wasāʾil
   * had left out, and every report in it is quoted from something older. Without this a reader
   * sees a report attributed to a book of 1902 and no sign that its content is centuries earlier.
   *
   * The data has carried it on 21,946 of that shelf's 22,351 rows since the shelf existed, with a
   * `Report.citedFrom` field declaring it and tests pinning four shapes of it, and nothing between
   * the corpus and this component ever named it.
   *
   * Shown verbatim, including the 649 rows that read `بهذا الإسناد` — "by this chain", meaning the
   * report before rather than a book. That is what the edition says.
   */
  citedFrom?: string;
  /**
   * WHICH BRANCH HOLDS THIS REPORT'S COLLECTION — added 2026-08-19.
   *
   * This list mixes shelves. A figure named in Bukhārī, the Mustadrak and the Musnad of Zayd gets
   * all three here, one under another, and until now the only thing distinguishing them was a ref
   * — `Bukhari 4662`, `mustadrak 1:228`, `musnad-zayd 12` — which a reader has to already know how
   * to read. The Sources panel gives each shelf its `heldBy`, and that is another panel.
   *
   * Absent where the pipeline does not place the collection, and then nothing is shown. A missing
   * label reads as "this was not said"; a wrong one tells a Zaydi reader their collection is Sunni.
   */
  branch?: 'sunni' | 'twelver' | 'zaydi' | 'ibadi';
  /**
   * WHERE THIS REPORT SITS IN ITS COLLECTION — the book, and the chapter where it is known.
   *
   * A citation like `Bukhari 4662` tells a reader nothing about what they are looking at.
   * **Al-Bukhārī's arrangement is an argument**: he files a report under a chapter heading he
   * wrote, and the heading is how he says what he takes it to mean. Shown here, `Bukhari 4662`
   * becomes a report in the Book of Military Expeditions, under a chapter about a named battle.
   *
   * `inferred` is carried rather than smoothed away. Three quarters of these were located
   * outright and carry a `bab`; the rest take their book from the reports either side, in a shelf
   * that runs in the book's own order, and get no chapter. The panel says which, because the two
   * are different claims and one in two hundred and fifty of the located ones is measurably wrong.
   *
   * Bukhārī only today — it is the one shelf whose structure has been joined — and absent
   * everywhere else rather than guessed.
   */
  place?: { kitab?: number; name: string; bab?: string; nameEn?: string; babEn?: string; inferred?: true };
  /**
   * THE COMPILER'S OWN VERDICT ON THIS REPORT — added 2026-08-19.
   *
   * The doc on `alsoIn` above ends "the field had nothing to carry because nothing had looked",
   * about al-Tirmidhī. **A shelf that carries them arrived:** al-Ḥākim's Mustadrak, where 4,495 of
   * 8,907 reports come with the sentence he wrote about them — more than al-Tirmidhī's 2,882.
   *
   * `Grade` is imported rather than re-spelt here. Twelve terms typed a second time is a second
   * list nothing keeps in step, and this one is already declared twice under two domain names.
   */
  grade?: Grade;
  /**
   * WHO GAVE THE VERDICT, AND WHY THIS IS NOT OPTIONAL BESIDE IT.
   *
   * A grade shown bare reads as this library's. It never is: it is one scholar's opinion of
   * another's report, and `reportProblems` refuses a grade without a grader for that reason. The
   * failure is not hypothetical — the file this shelf comes from carries a SECOND grading column,
   * naming nobody, that contradicts al-Ḥākim on 1,212 of these same reports and calls 26 of them
   * fabricated where he calls them sound. It is not shipped, and this field is why it could not be.
   */
  gradedBy?: string;
  /**
   * The verdict in his own words, which the one-word classification is a reading of.
   *
   * "sahih" stands for *"this is a sound hadith on the criteria of the two Shaykhs, for ʿAbd Allāh
   * b. Nāfiʿ is trustworthy"*, and a reader who wants the difference should see it rather than take
   * the label. Arabic, so it carries its own `lang` and `dir` — the same reason `citedFrom` does.
   */
  gradeText?: string;
  /**
   * The other figures this report names, from the build's own index.
   *
   * These are what makes an untranslated page navigable. A report on Abraham's panel that
   * also names Ishmael, Mecca and the Kaaba is a report about the building of the Kaaba, and
   * a reader gets that much without a word of Arabic.
   */
  also?: { id: string; name: string }[];
  /**
   * How this figure is in this report — see the artifact type.
   *
   * Absent means the report's own text names them, which is the ordinary case and a prophet's
   * whole list. `narrated` means they are a link in the chain and the report may be about
   * someone else; `both`, that they are named in a report they also passed on.
   */
  via?: 'narrated' | 'both';
  /**
   * HADEETHENC'S ENGLISH OF THIS REPORT — added 2026-08-20, and the first English any report on
   * these shelves has ever carried.
   *
   * `Report.text` is empty on all 294,686 rows, so until now a reader who does not read Arabic met
   * the entire hadith apparatus as an opaque block. `map-hadeeth-english.ts` joins HadeethEnc's
   * 2,328 entries to the reports whose Arabic they match and reaches **1,960**.
   *
   * ABSENT ON THE GREAT MAJORITY, and that is the honest state rather than a gap to be filled with
   * something: no other English on the table may be shown. Goldsack's larger set is in copyright
   * until 2028 and every modern translation of the Ṣaḥīḥayn runs past 2049.
   *
   * It is NOT this library's translation, and `entry` is here so a reader can be sent to the
   * source rather than take ours for it.
   */
  en?: { text: string; entry: number };
  /**
   * THIS REPORT STOPS MID-WORD — added 2026-08-20.
   *
   * Athar's file cuts one matn in nine at exactly 200 characters, «…يعيد مسح ر», and **469 reports
   * across five shelves** arrive that way. Nothing in this project can undo it: the characters
   * after the two-hundredth were never delivered.
   *
   * WHY IT IS SHOWN RATHER THAN HIDDEN. The two thinnest branches this library has are the two
   * hardest hit — the Zaydi shelf a quarter, the Ibadi a seventh — so a reader of exactly the
   * traditions least represented here meets a fragment most often, and would take it for a short
   * hadith. That is the shape of thing this panel already refuses elsewhere: a grade never appears
   * without its grader, a translation never without its translator.
   */
  cut?: boolean;
  /**
   * WHOSE WORDS THESE ARE — added 2026-08-20, on 16,919 reports that never said.
   *
   * A hadith collection does not only carry the Prophet. A chain can stop at a COMPANION
   * (mawqūf) or at a SUCCESSOR (maqṭūʿ), and then the report is that man's opinion or practice —
   * not the Prophet's words. The thirteen shelves taken from hadith-kg hold **12,689 mawqūf and
   * 4,230 maqṭūʿ, 16.9% of 99,920**, and the source states it for every single row in a `type`
   * column the parser walked straight past.
   *
   * So a reader met a Companion's ruling laid out exactly like a prophetic hadith, under a
   * collection name they had every reason to trust. Saʿīd b. Manṣūr's Sunan is 78.6% that.
   *
   * NOT A GRADE, and shown so it cannot be read as one — a report that stops at a Companion can be
   * perfectly sound. This is the same principle as `gradedBy` and `cut` above: the panel states
   * what a report IS before a reader has to infer it.
   *
   * Absent on the other shelves because their suppliers state no layer, and absent is the honest
   * answer there. It must never be filled in with `marfu`: that would claim the Prophet's
   * authority for 194,766 reports on the strength of a missing column.
   */
  layer?: HadithLayer;
}

export interface ReportsListProps {
  /** The figure these are related about, for the heading. */
  name: string;
  reports: PanelReport[];
  /** How many exist in total — may exceed what has loaded. */
  total: number;
  /**
   * Whether the fetch for these failed, as distinct from the figure having none.
   *
   * The hook has always known the difference and the caller used to drop it, so a failed fetch
   * removed the section entirely — heading, count and all — from a figure the bundle says has 1,599.
   */
  failed?: boolean;
  /**
   * How many of that total the figure only PASSED ON, counted over the whole list by the build.
   *
   * Absent on a report file written before `narrated` existed, and absent for a figure with none —
   * both fall back to counting the loaded page, which is what this did for everybody until
   * 2026-08-22 and is why the note never appeared on a busy narrator. See `schema/artifacts.ts`.
   */
  narrated?: number;
  /**
   * Whether a further page of this figure's reports exists, and how to ask for it.
   *
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * The list already grows twenty at a time on scroll; **the FILE behind it did not**, and one
   * figure's file reached 19.2 MB on 2026-08-20 after eight books were added in a day. So the
   * same sentinel that asks for twenty more rows now also asks for the next two hundred when it
   * has run out of loaded ones.
   *
   * Optional, because a caller with everything in hand — a test, a fixture — should not have to
   * say so.
   */
  hasMore?: boolean;
  loadMore?: () => void;
  /**
   * Open a quoted ayah, by citation.
   *
   * The reason the citations are worth drawing at all: a reader meeting Arabic with no
   * English can follow the quotation to a verse the library DOES carry in English, and read
   * the two together. It is the one place a hadith becomes checkable without a translation.
   */
  onOpenRef?: (ref: string) => void;
  /**
   * Travel to another figure this report names.
   *
   * The same handler the passages use, so a name reached through a hadith and a name reached
   * through a verse behave identically — one way to move between figures, not two.
   */
  onJump?: (id: string) => void;
  /**
   * The machine translator, for tests. Omitted, the browser's on-device one — see
   * i18n/translate.ts, which is also the seam the passages use. One implementation, two
   * callers; a second translator here would be a second set of caveats to keep honest.
   */
  translator?: TextTranslator | null;
}

/**
 * The i18n key naming each branch.
 *
 * A map rather than a template — `panel.branch${capitalise(b)}` would build a key from data, and a
 * key that does not exist renders as itself, so a new branch would put the literal string
 * `panel.branchIsmaili` in front of a reader instead of failing where somebody can see it.
 */
const BRANCH_LABEL = {
  sunni: 'panel.branchSunni',
  twelver: 'panel.branchTwelver',
  zaydi: 'panel.branchZaydi',
  ibadi: 'panel.branchIbadi',
} as const;

/**
 * The i18n key naming each verdict, for the same reason `BRANCH_LABEL` is a map.
 *
 * `Record<Grade, …>` rather than a loose object: a term added to the vocabulary in
 * `schema/reports.ts` is then a type error here until it has a string, instead of rendering the
 * literal key `panel.gradeMuallaq` in front of a reader. That is the whole difference between a
 * gap that fails where somebody can see it and one that ships.
 */
const GRADE_LABEL = {
  'hasan-sahih': 'panel.gradeHasanSahih',
  'hasan-gharib': 'panel.gradeHasanGharib',
  sahih: 'panel.gradeSahih',
  hasan: 'panel.gradeHasan',
  gharib: 'panel.gradeGharib',
  daif: 'panel.gradeDaif',
  munkar: 'panel.gradeMunkar',
  mursal: 'panel.gradeMursal',
  mudtarib: 'panel.gradeMudtarib',
  mukhtasar: 'panel.gradeMukhtasar',
  mansukh: 'panel.gradeMansukh',
  malul: 'panel.gradeMalul',
  /* `satisfies` rather than a type annotation: the annotation would widen every value to `string`
     and `t` takes a key, not a string. This keeps the literals AND still fails on a missing term. */
} as const satisfies Record<Grade, string>;

/**
 * The i18n key naming each layer, for the same reason `GRADE_LABEL` is a map.
 *
 * `satisfies Record<HadithLayer, …>` so a fifth layer added to `schema/hadith-layer.ts` is a type
 * error here rather than the literal key `panel.layerMursal` in front of a reader.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * THE LABEL SAYS WHAT THE TERM MEANS, NOT THE TERM. A chip reading "mawqūf" tells a reader who
 * already knows what mawqūf is something they knew, and tells everybody else nothing — while the
 * whole point of the field is that a reader who does NOT know is currently taking a Companion's
 * ruling for the Prophet's words. So the chip reads *stops at a Companion*, and the technical term
 * with its Arabic sits in the title for the reader who wants it.
 */
const LAYER_LABEL = {
  marfu: 'panel.layerMarfu',
  qudsi: 'panel.layerQudsi',
  mawquf: 'panel.layerMawquf',
  maqtu: 'panel.layerMaqtu',
} as const satisfies Record<HadithLayer, string>;

export function ReportsList({
  name, reports, total, failed, narrated, hasMore, loadMore, onOpenRef, onJump, translator,
}: ReportsListProps): React.JSX.Element {
  const { t, lang } = useLang();
  const heading = t('panel.reports', { name });
  /* The step is `REPORTS_PER_STEP`, declared beside the page size it divides — see there for why
     twenty, and why the two numbers only mean anything together. */
  const { shown, sentinel } = useGrowOnScroll(reports.length, REPORTS_PER_STEP);
  /*
   * WHEN THE ROWS RUN OUT, ASK FOR THE NEXT PAGE OF THE FILE.
   *
   * Two limits used to be one: the list drew twenty at a time and the file arrived whole. That was
   * fine until a figure's file reached 19.2 MB. Now the same scroll that asks for twenty more rows
   * asks for another PAGE of reports when it has shown everything it has — forty of them, which is
   * two of these twenties, so there is a screen in hand while the next arrives.
   *
   * `shown >= reports.length` rather than `>`: `useGrowOnScroll` caps `shown` at the length, so
   * equality IS the end of the list. Written as `>` it would never fire, and the panel would stop
   * at page one with no sign that it had.
   */
  useEffect(() => {
    if (hasMore && loadMore && shown >= reports.length) loadMore();
  }, [hasMore, loadMore, shown, reports.length]);
  /*
   * NAMED IN versus PASSED ON, counted over the WHOLE list — which took two goes.
   *
   * A comment here used to state that requirement and the line under it did not meet it: it counted
   * `reports`, which is the loaded SLICE — paginated by `loadMore` two lines above, capped at 200
   * and ordered so the reports that NAME the figure lead. So for every busy narrator the cap fell
   * before a single narrated row, the count was 0, and the note below never rendered — on precisely
   * the figures it exists for. Across all 173 report artifacts: 3,982 rows are `narrated`, 356
   * `both`, 11,289 named; and of the eight busiest figures NOT ONE carried a narrated row in its
   * slice. `e2e/reports.spec.ts` was red on this and was right to be.
   *
   * FIXED 2026-08-22 by having the build count it, and TWO THINGS I RECORDED ALONG THE WAY WERE
   * WRONG, both worth keeping because the next session will reach for the same two.
   *
   * The first: I wrote it could not be verified in a worktree, `build-web-data.ts` needing
   * `corpora/hadith.json`. It does — but the ANSWER does not need the corpus, because that stage
   * already wrote it. `artifacts/reports/<node>.json` is page 0 of N with `<node>.p1…pN.json`
   * beside it, so the whole list is on disk wherever the artifacts are. Read across all eight of
   * Abu Hurayra's pages: **1,294 narrated of 1,599**, where page 0 alone says none, and 80 `both`
   * counting as named exactly as before. Aisha 579 of 1,168; Ibn Umar 610 of 920.
   *
   * The second: I put it on `Bundle`, as a `reportNarrated` map beside `reportCounts`, and
   * `pipeline/lib/both-surfaces.test.ts` refused — a `Bundle` field read by one surface and not
   * the other is the defect that test exists for, and the `PLUMBING` exemption list is capped at 8
   * so there was no room to declare it plumbing either. **Both refusals were right.** The static
   * pages do not draw this note, so it is not a `Bundle` datum: it describes ONE figure's report
   * list, and `ReportsArtifact` is that list's own file — fetched when a panel opens, where the
   * bundle is fetched by every reader for every page.
   *
   * So the count where the build has sent one, and what this holds otherwise. Falling back to
   * counting `reports` keeps exactly the old behaviour for a file written before the field, which
   * is also what a figure who narrated none of theirs looks like: the note is absent either way,
   * correctly, because there is nothing to disclose.
   *
   * WHAT WAS ACTUALLY RUN, since the stage itself still needs the corpus: the 173 report artifacts
   * on disk were rewritten through `reportsJson` with `narrated` added — the same serializer and
   * the same field the build now writes — after re-serializing every one of them UNCHANGED and
   * demanding byte-identical output first, so the only difference is the field. `e2e/reports.spec`
   * then went green, and red again when the field was stripped back out. The pipeline line above
   * remains unrun here; everything downstream of it is proven in a browser.
   */
  const narratedCount = narrated ?? reports.filter(r => r.via === 'narrated').length;
  const namedCount = (narrated === undefined ? reports.length : total) - narratedCount;
  return (
    <section className="reports" aria-label={heading}>
      {/*
        * COLLAPSED, AND THAT IS NOT A DESIGN PREFERENCE.
        *
        * Abraham has 578 reports. Rendered open, they push the figure's own passages off the
        * screen — and the passages are the thing this project exists to show. Collapsed, the
        * section still announces itself with a heading and a count, which is what a reader
        * needs to know it is there. `details` gives that for free with real keyboard and
        * screen-reader behaviour, rather than a div pretending to be a disclosure.
        */}
      <details className="reports-disclosure">
        <summary>
          <h3>
            {heading}
            {' '}
            <span className="reports-count">{total}</span>
          </h3>
        </summary>
        {/*
          * THE FETCH FAILED, AND THE COUNT IN THE HEADING IS STILL TRUE.
          *
          * `useNodeReports` has always distinguished "this figure has no hadith" from "we could not
          * reach them", and says why in its own header: the bundle already told the reader there
          * were some. The CALLER threw that away — `App.tsx` passed this panel only when rows had
          * arrived — so a failed fetch removed the whole section, heading and count and all. A
          * reader opening Abu Hurayra while the edge hiccupped saw a figure with no hadith at all,
          * where the bundle knows there are 1,599.
          *
          * The heading and its count stay, because both are read from the bundle and neither has
          * failed. Only the list is missing, and this says so in the words the rest of the app uses
          * when something could not be read.
          */}
        {failed && reports.length === 0 && (
          <p className="reports-failed" role="status">{t('shared.couldNotRead')}</p>
        )}
        {/* Not a footnote. A reader meeting Arabic with no English is told why, here. */}
        {!failed && <p className="reports-note">{t('panel.reportsArabicOnly')}</p>}
        {/*
          * AND WHOSE NUMBERS THESE ARE. `Bukhari 4206` looks like a citation a reader can carry
          * elsewhere and it is not: the numbering is Islam Ware’s own sequence. Measured against two
          * independent digitisations — Athar, and the Leeds/King Saud corpus in sunnah.com’s
          * numbering — 1,972 reports matched by TEXT and four of their numbers agreed.
          *
          * The text is sound; SOURCES.md records 94% of an independent Sahih Muslim found inside
          * ours. It is the reference that travels badly, and a reference that looks well-formed and
          * points somewhere else is worse than none.
          */}
        {needsNumberingNote(reports.map(r => r.ref)) && (
          <p className="reports-note">{t('panel.reportsNumbering')}</p>
        )}
        {/*
          * WHAT THE HEADING DOES NOT SAY. "Also related about Abu Hurayra" over 1,825 reports
          * is not wrong so much as incomplete: 1,374 of them are reports he TRANSMITTED. That
          * he is the most prolific narrator in the collection is a fact about the apparatus,
          * not about his life, and a reader should meet it here rather than infer it.
          *
          * Shown only where the list actually mixes the two. A prophet's list is entirely
          * "named in" and a line saying so would be noise.
          */}
        {narratedCount > 0 && namedCount > 0 && (
          <p className="reports-split">
            {t('panel.reportsSplit', { named: namedCount, narrated: narratedCount })}
          </p>
        )}
        <ol className="reports-list">
        {reports.slice(0, shown).map(r => (
          <li key={r.ref} className="report">
            <cite className="report-ref">{r.ref}</cite>
            {/*
              * IMMEDIATELY AFTER THE REF, because it is what the ref does not say. Everything else
              * on this row is a property of the report; this is what shelf it came off, and a
              * reader scanning a mixed list needs it at the same glance as the citation.
              *
              * Not a control. There is nowhere in this panel to send somebody who clicks a branch,
              * and a button that looks live and does nothing is worse than a fact — the same
              * reasoning `alsoIn` below is written under.
              */}
            {r.branch && (
              <span className={`report-branch report-branch-${r.branch}`} title={t('panel.branchTitle')}>
                {t(BRANCH_LABEL[r.branch])}
              </span>
            )}
            {/*
              * WHOSE WORDS THESE ARE, beside the branch, because it is the same kind of fact: the
              * branch says which community keeps the report, this says whom the chain reaches.
              *
              * SHOWN WHENEVER THE SOURCE STATES IT, INCLUDING WHEN IT IS THE PROPHET — and that is
              * the deliberate part. Marking only the exception would be quieter, and it is what
              * `via` above does, but silence there has one meaning and here it would have two:
              * *this reaches the Prophet* on thirteen shelves and *nobody said* on the other
              * forty-six. A reader cannot tell which shelf they are looking at, so an unmarked
              * report would read as prophetic on shelves that never claimed any such thing.
              *
              * Stating it always keeps silence meaning exactly one thing — not stated — and every
              * positive claim explicit. The weight differs instead of the presence: reaching the
              * Prophet is the ordinary case and sits quiet, and a chain that stops short is marked
              * so a reader cannot pass it without seeing.
              */}
            {r.layer && (
              <span
                className={`report-layer report-layer-${reachesTheProphet(r.layer) ? 'prophet' : 'stops'}`}
                title={`${layerTerm(r.layer).translit} · ${layerTerm(r.layer).arabic}`}
              >
                {t(LAYER_LABEL[r.layer])}
              </span>
            )}
            {/* Marked only where the heading would otherwise mislead: this one he passed on,
                and it may be about someone else entirely. A report that names him needs no
                marker — that is what the heading already says. */}
            {r.via === 'narrated' && (
              <span className="report-via">{t('panel.reportsNarrated', { name })}</span>
            )}
            {/*
              * BESIDE THE CITATION, because it is a property of this report rather than
              * something it points at — the same report is in the other collection too. Read
              * as text, not offered as a control: there is nowhere in this panel to send a
              * reader, and a button that looks live and does nothing is worse than a fact.
              */}
            {r.alsoIn && r.alsoIn.length > 0 && (
              <span className="report-alsoin" title={t('panel.reportsAgreedTitle')}>
                {t('panel.reportsAgreed')} <span className="report-alsoin-refs">{r.alsoIn.join(', ')}</span>
              </span>
            )}
            {/*
              * WHERE IN THE BOOK — beside the citation, because that is what it qualifies.
              *
              * The titles are Arabic and carry their own `lang` and `dir` for the reason
              * `citedFrom` does one comment down: inherited direction puts a right-to-left
              * title's punctuation on the wrong side.
              *
              * An inferred placing says so in its tooltip rather than looking identical to a
              * located one. It also has no chapter, which is the visible difference — a reader
              * who sees a book and no chapter is seeing the weaker claim.
              */}
            {r.place && (() => {
              /*
               * ENGLISH WHERE THE SOURCE GIVES ONE, and only there.
               *
               * The Twelver shelf's index carries an English title for every book and chapter; the
               * Sunni structure is read off Arabic printings and has none. So an English reader
               * gets the English on one shelf and the Arabic on the other — which is the sources
               * rather than a choice, and better than withholding the English to look even.
               *
               * A title shown in English must NOT carry `lang="ar" dir="rtl"`, or it renders
               * right-to-left with its punctuation on the wrong side — the same defect the Arabic
               * needs those attributes to avoid.
               */
              const english = lang === 'en';
              const kitab = (english && r.place.nameEn) || r.place.name;
              const bab = (english && r.place.babEn) || r.place.bab;
              const kitabAr = kitab === r.place.name;
              const babAr = bab === r.place.bab;
              return (
                <span
                  className={`report-place${r.place.inferred ? ' report-place-inferred' : ''}`}
                  title={t(r.place.inferred ? 'panel.reportsPlaceInferredTitle' : 'panel.reportsPlaceTitle')}
                >
                  <span className="report-place-kitab" {...(kitabAr ? { lang: 'ar', dir: 'rtl' as const } : {})}>{kitab}</span>
                  {bab && (
                    <>
                      {' · '}
                      <span className="report-place-bab" {...(babAr ? { lang: 'ar', dir: 'rtl' as const } : {})}>{bab}</span>
                    </>
                  )}
                </span>
              );
            })()}
            {/*
              * BESIDE `alsoIn` FOR THE SAME REASON IT SITS THERE: both are properties of this
              * report rather than somewhere to send a reader, and neither is offered as a control.
              *
              * The value is Arabic — a book title as the edition prints it, vocalised — so it
              * carries `lang` and `dir` of its own. Without them it inherits the panel's direction
              * and a right-to-left title renders with its punctuation on the wrong side, which is
              * the defect `.said:lang(ar)` exists to prevent one layer up.
              */}
            {r.citedFrom && (
              <span className="report-citedfrom" title={t('panel.reportsQuotedFromTitle')}>
                {t('panel.reportsQuotedFrom')}{' '}
                <span className="report-citedfrom-work" lang="ar" dir="rtl">{r.citedFrom}</span>
              </span>
            )}
            {/*
              * WHO ELSE IS IN IT — ABOVE THE ARABIC, because that is the only part most
              * readers of this page can read. Put below, it would sit under a paragraph of
              * script they cannot parse and would never be looked for.
              *
              * Not links out of the panel: the same onJump the passages use, so travelling by
              * a name works one way whether the name was found in a verse or in a report.
              */}
            {r.also && r.also.length > 0 && (
              <p className="report-also">
                <span className="report-also-label">{t('panel.reportsAlso')}</span>
                {r.also.map(n => (
                  <button
                    key={`${r.ref}-${n.id}`}
                    type="button"
                    className="report-also-name"
                    onClick={() => onJump?.(n.id)}
                    disabled={!onJump}
                  >
                    {n.name}
                  </button>
                ))}
              </p>
            )}
            {/* lang and dir are not decoration: without them a screen reader pronounces
                Arabic with English phonemes, which is noise where the text should be. */}
            <p className="report-text" lang="ar" dir="rtl">{r.ar}</p>
            {/*
              * AFTER the Arabic and never instead of it. The text is what the supplier delivered
              * and a reader is entitled to all of it; what they are also entitled to is the fact
              * that it stops early. Rendered as a sentence rather than an icon, because "this is
              * a fragment" is not something a symbol can say unambiguously.
              */}
            {r.cut && (
              <p className="report-cut" title={t('panel.reportsCutTitle')}>{t('panel.reportsCut')}</p>
            )}
            {/*
              * THE ENGLISH, WHERE ONE EXISTS — and never without saying whose it is.
              *
              * `schema/reports.ts` requires `edition` beside any human translation and `gradedBy`
              * beside any grade, on one principle: unattributed, it reads as this library's own
              * work. A translation is a reading, and a reader deciding what to make of a hadith is
              * owed the knowledge that somebody chose these English words.
              *
              * The link goes to HadeethEnc's own entry, so the claim can be checked at its source.
              */}
            {r.en && (
              <div className="report-en">
                <p className="report-en-text">{r.en.text}</p>
                <p className="report-en-who">
                  <a
                    className="report-en-source"
                    href={`https://hadeethenc.com/en/browse/hadith/${r.en.entry}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {t('panel.reportsEnglishFrom')}
                  </a>
                </p>
              </div>
            )}
            {/*
              * THE COMPILER'S VERDICT, AFTER THE REPORT AND NOT BESIDE THE CITATION.
              *
              * The other markers up there — branch, "also in", "quoted from" — are facts about
              * where the report sits. This is a judgement ABOUT it, and it belongs where he made
              * it: after the text, the way the book itself has it.
              *
              * HIS NAME IS INSIDE THE SENTENCE, never a label the reader has to hover for. A grade
              * shown bare reads as this library's own verdict on a report, and this library has
              * none. Rendered only with `gradedBy` present, which the schema already guarantees —
              * a belt on top of the braces, because an unattributed verdict is the one failure
              * mode here that a reader could not detect.
              */}
            {r.grade && r.gradedBy && (
              <p className="report-grade" title={t('panel.reportsGradedTitle')}>
                <span className="report-grade-who">{t('panel.reportsGraded', { who: r.gradedBy })}</span>
                {' '}
                <span className="report-grade-term">{t(GRADE_LABEL[r.grade])}</span>
                {/* His own sentence, which the term above is a reading of. Arabic, so it carries
                    its own direction — inheriting the panel's would put the punctuation on the
                    wrong side, the defect `.said:lang(ar)` exists to prevent one layer up. */}
                {r.gradeText && (
                  <span className="report-grade-text" lang="ar" dir="rtl">{r.gradeText}</span>
                )}
              </p>
            )}
            {/*
              * THE ONLY READING THERE IS, which makes this different from a verse.
              *
              * On a passage, the machine reading is optional colour beside an authoritative
              * English. Here there is no English at all and none can be licensed, so for a
              * reader who does not read Arabic this is the difference between a page they can
              * use and a page they cannot. That raises the value of offering it and raises
              * the cost of getting it wrong, which is why it is still opt-in and still
              * arrives marked as a rough machine reading — the component's own rule.
              *
              * The whole report is translated, chain included, because the chain is part of
              * what is on the screen: "Abu Nu'aym narrated to us, from Zuhayr" is a sentence
              * an English reader currently cannot get at, not noise to be stripped.
              */}
            <VerseTranslation text={r.ar} from="ar" translator={translator} />
            {/* The ayahs this report quotes, resolved from its own text — the one link out of
                a collection that carries no English. Buttons, not links: they open the reader
                in place rather than navigating away from the figure. */}
            {r.cites && r.cites.length > 0 && (
              <p className="report-cites">
                <span className="report-cites-label">{t('panel.reportsCites')}</span>
                {r.cites.map(ref => (
                  <button
                    key={`${r.ref}-${ref}`}
                    type="button"
                    className="report-cite"
                    onClick={() => onOpenRef?.(ref)}
                    disabled={!onOpenRef}
                  >
                    {ref}
                  </button>
                ))}
              </p>
            )}
            {r.isnad.length > 0 && (
              <details className="report-chain">
                <summary>{t('panel.reportsChain')}</summary>
                <ol lang="ar" dir="rtl">
                  {r.isnad.map((n, i) => (
                    <li key={`${r.ref}-${i}`}>
                      {n}
                      {/*
                        * WHO THAT IS, under the name and not in place of it.
                        *
                        * The chain says `ابن عجلان`; the register says Muḥammad b. ʿAjlān
                        * al-Qurashī, died 148. Both are shown because they are two different
                        * things — what the compiler wrote, and who he meant — and replacing the
                        * first with the second would be editing the text to make it clearer,
                        * which this library does not do.
                        *
                        * Rendered only where it differs from the name, so a chain whose links are
                        * already full names does not repeat itself down the page.
                        */}
                      {r.who?.[i] && r.who[i] !== n && (
                        <span className="report-chain-who">{r.who[i]}</span>
                      )}
                    </li>
                  ))}
                </ol>
                {r.who?.some(Boolean) && (
                  <p className="report-chain-note">{t('panel.reportsChainWho')}</p>
                )}
              </details>
            )}
          </li>
          ))}
        {/* Reaching the end IS the request for more — see useGrowOnScroll. A "load more"
            button would ask the reader to restate what their scrolling already said. */}
        {shown < reports.length && <li className="reports-more" ref={sentinel} aria-hidden="true" />}
        </ol>
      </details>
    </section>
  );
}
